import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/**
 * §19.2 — Birincil (accent dolu) · İkincil (border) · Yıkıcı (kırmızı outline → onayda dolu).
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[var(--radius-sm)] ' +
    'font-medium tracking-[-0.005em] transition-[background-color,border-color,color] duration-150 ' +
    'disabled:pointer-events-none disabled:opacity-45 ' +
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        /*
         * Birincil düğme gölgesiz. Gölge + dolu renk birlikte düğmeyi
         * "kabartma" gösteriyor; sade bir dolu blok daha modern ve karttaki
         * diğer öğelerle aynı düzlemde duruyor. Hover'da opaklık değil RENK
         * değişiyor — opaklık zemini sızdırıp rengi kirletiyordu.
         */
        // KSKLC Takip'in ana düğmesi: indigo → menekşe gradyan; iki temada da beyaz yazı okunuyor
        primary:
          'text-white shadow-sm [background-image:linear-gradient(135deg,#4f46e5_0%,#7c3aed_100%)] hover:brightness-110',
        secondary: 'border border-border-strong bg-bg-elevated text-fg hover:bg-bg-sunken',
        ghost: 'text-fg-muted hover:bg-bg-sunken hover:text-fg',
        destructive: 'border border-danger/40 text-danger hover:bg-danger hover:text-danger-fg',
        link: 'text-accent underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-7 px-2.5 text-xs [&_svg]:size-3.5',
        md: 'h-8.5 px-3.5 text-[0.8125rem] [&_svg]:size-4',
        lg: 'h-10 px-5 text-sm [&_svg]:size-4',
        icon: 'size-8.5 [&_svg]:size-4',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, disabled, children, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && (
        <span
          className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden="true"
        />
      )}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';
